<template>
  <AppLayout>
    <div class="container mx-auto mt-5 pt-3">
      <!-- Nav tabs -->
      <Tabs value="upload">
          <TabList>
              <Tab value="upload">
                  {{ $t('messages.upload.transactionsUpload') }}
              </Tab>
          </TabList>
          <!-- Tab panes -->
          <TabPanels>
              <TabPanel value="upload">
                  <div>
                      <h5 class="mb-3 mt-4 text-lg">{{ $t('messages.upload.transactionsUploadChoose') }}</h5>
                      <Message v-if="errorMsg" severity="danger">{{ errorMsg }}</Message>

                      <Message v-if="uploadDisabled" severity="warning">{{ $t('messages.upload.disabled') }}</Message>

                      <form class="flex gap-2 mb-3">
                        <div class="flex flex-col gap-2">
                          <div class="flex gap-4">
                            <FileUpload
                                mode="basic"
                                name="file"
                                accept=".xlsx"
                                :maxFileSize="16777216"
                                :auto="false"
                                :customUpload="true"
                                @select="fileSelected"
                                @clear="clearSelectedFile"
                                :chooseLabel="$t('messages.upload.pickDocument')"
                                :disabled="uploadDisabled"
                            />
                            <Button @click="uploadFile()" :disabled="!selectedFile || uploadInProgress" :loading="uploadInProgress">{{ $t('messages.upload.submit') }}</Button>
                          </div>
                        </div>
                      </form>

                      <ProgressBar v-if="uploadInProgress" :value="progress" class="mt-2 mb-3" />

                      <div class="mt-8 shadow-md rounded-md border" v-if="files.length || showSpinnerGet">

                        <div class="flex justify-between bg-gray-200 dark:bg-gray-800 p-4 rounded-t-md font-bold">
                            <h5>{{ $t('messages.upload.uploadedFiles') }}</h5>
                        </div>

                        <ProgressSpinner v-if="showSpinnerGet" style="width: 50px; height: 50px" strokeWidth="8" />
                        <div v-else class="overflow-x-auto">
                          <table class="table-auto w-full text-left text-sm">
                            <thead class="border-b">
                              <tr>
                                <th scope="col" class="p-2 w-12"><span class="flex justify-center">#</span></th>
                                <th scope="col" class="p-2 w-140">{{ $t('messages.upload.fileName') }}</th>
                                <th scope="col" class="p-2 w-40">
                                  <span class="flex justify-center">{{ $t('messages.upload.transactions_count') }}</span>
                                </th>
                                <th scope="col" class="p-2 w-40">
                                  <span class="flex justify-center">{{ $t('messages.upload.duplicates_skipped') }}</span>
                                </th>
                                <th scope="col" class="p-2 w-40">
                                  <span class="flex justify-center">{{ $t('messages.upload.geocoded') }}</span>
                                </th>
                                <th scope="col" class="p-2 w-40">
                                  <span class="flex justify-center">{{ $t('messages.upload.ehrLoaded') }}</span>
                                </th>
                                <th scope="col" class="p-2 w-40">
                                  <span class="flex justify-center" v-tooltip.top="$t('messages.upload.check.columnHint')">{{ $t('messages.upload.check.column') }}</span>
                                </th>
                                <th scope="col" class="p-2">
                                  <span class="flex justify-center">{{ $t('messages.upload.batchProgress') }}</span>
                                </th>
                                <th scope="col" class="p-2 w-20">{{ $t('messages.upload.actions') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(file, index) in files" :key="file.id" class="border-b">
                                <td class="p-2 text-center">
                                    {{ index + 1 }}
                                </td>
                                <td class="p-2 flex flex-col">
                                  {{ file.filename }}
                                  <div class="text-xs text-gray-600">
                                    <span :title="$filters.formatDateTime(file.created_at)">{{ file.created_at_human }}</span>
                                  </div>
                                </td>
                                <td class="p-2 text-center">{{ file.items_processed }} / {{ file.items_total }}</td>
                                <td class="p-2 text-center" :class="{ 'text-gray-300': file.items_skipped_duplicates === 0 }">{{ file.items_skipped_duplicates ?? 0 }} / {{ file.items_total }}</td>
                                <td class="p-2 text-center" :class="{ 'text-gray-300': file.items_geocoded_count === 0 }">
                                  <span
                                    v-if="canOpenGeocodingDetails(file)"
                                    class="cursor-pointer text-blue-500"
                                    @click="openDetailsDialog(file.id, 'geocoding')"
                                  >
                                    {{ file.items_geocoded_count ?? 0 }} / {{ file.items_processed }}
                                  </span>
                                  <span v-else>
                                    {{ file.items_geocoded_count ?? 0 }} / {{ file.items_processed }}
                                  </span>
                                </td>
                                <td class="p-2 text-center" :class="{ 'text-gray-300': file.ehr_items_processed === 0 }">
                                  <span
                                    v-if="canOpenEhrDetails(file)"
                                    class="cursor-pointer text-blue-500"
                                    @click="openDetailsDialog(file.id, 'ehr')"
                                  >
                                    {{ file.ehr_items_processed ?? 0 }} / {{ file.items_processed }}
                                  </span>
                                  <span v-else>
                                    {{ file.ehr_items_processed ?? 0 }} / {{ file.items_processed }}
                                  </span>
                                </td>
                                <td class="p-2 text-center" :data-testid="`upload-map-check-${file.id}`">
                                  <button
                                    v-if="file.check"
                                    type="button"
                                    class="inline-flex items-center gap-1 cursor-pointer text-blue-500"
                                    @click="openCheckDialog(file)"
                                  >
                                    <span>{{ file.check.on_map }} / {{ file.check.file_rows }}</span>
                                    <!-- A mark is always rendered, so it never shifts the figure next to it; a missing row wins over a waiting one. -->
                                    <i
                                      v-if="file.check.unexplained"
                                      class="pi pi-exclamation-triangle text-orange-500"
                                      :data-testid="`upload-map-check-warning-${file.id}`"
                                      v-tooltip.top="$t('messages.upload.check.missingTooltip', { count: file.check.unexplained })"
                                    />
                                    <i
                                      v-else-if="file.check.pending_elsewhere"
                                      class="pi pi-clock text-gray-500"
                                      :data-testid="`upload-map-check-pending-${file.id}`"
                                      v-tooltip.top="$t('messages.upload.check.pendingTooltip', { count: file.check.pending_elsewhere })"
                                    />
                                    <i v-else class="pi pi-exclamation-triangle invisible" />
                                  </button>
                                  <span v-else class="text-gray-300" v-tooltip.top="$t('messages.upload.check.notChecked')">–</span>
                                </td>
                                <td class="p-2">
                                  <div v-if="file.batch_info && !file.is_processed" class="flex items-center">
                                    <ProgressBar :value="file.batch_info.progress ?? 0" style="height: 1rem" class="w-full mr-2"/>
                                  </div>
                                  <div v-else-if="file.batch_info && file.is_processed" class="text-xs text-gray-500 flex items-center justify-center">
                                    <i class="pi pi-check-circle" style="color: green; font-size: 1rem"></i>
                                  </div>
                                  <div v-else-if="file.is_processed && file.ehr_items_processed === 0" class="text-xs text-gray-500 flex items-center justify-center">
                                    <i class="pi pi-check-circle" style="color: green; font-size: 1rem"></i>
                                  </div>
                                  <div v-else-if="file.failed_at" class="text-xs text-gray-500 flex items-center justify-center">
                                    <i class="pi pi-times-circle" style="color: red; font-size: 1rem"></i>
                                  </div>
                                  <div v-else-if="file.processed_at === null && file.failed_at === null" class="text-xs text-gray-500 flex items-center justify-center">Alustamine...</div>
                                  <div v-else class="text-xs text-gray-500">-</div>
                                </td>
                                <td class="p-2 whitespace-nowrap">
                                  <div class="flex items-center">
                                    <span class="flex-1">
                                      <Button
                                        icon="pi pi-sync"
                                        severity="warning"
                                        text
                                        rounded
                                        size="small"
                                        class="mr-1"
                                        v-tooltip.top="$t('messages.upload.retry')"
                                        v-if="canRetry(file)"
                                        :disabled="uploadDisabled || file.retrying"
                                        :loading="file.retrying"
                                        @click="retryProcessing(file)"
                                      />
                                    </span>
                                    <span class="flex-1">
                                      <Button
                                        icon="pi pi-trash"
                                        severity="danger"
                                        text rounded
                                        size="small"
                                        v-tooltip.top="$t('messages.upload.delete')"
                                        :disabled="uploadDisabled || file.deleting"
                                        :loading="file.deleting"
                                        @click="deleteFileConfirm(file)"
                                      />
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                      </div>

                      <ConfirmDialog group="deleteConfirm" />

                      <Dialog v-model:visible="showDetailsDialog" :header="uploadDetail?.filename" :style="{ width: '80rem' }">
                        <div v-if="!currentErrorType" class="text-surface-700 dark:text-surface-400 block mb-2">
                          {{ uploadDetail?.batch_error_log }}
                        </div>
                        <div v-else>
                          <div v-if="loadingErrors" class="flex justify-center items-center py-8">
                            <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" />
                          </div>
                          <div v-else-if="errorList.length === 0" class="text-surface-700 dark:text-surface-400 block mb-2">
                            {{ $t('messages.upload.noErrors') }}
                          </div>
                          <div v-else class="overflow-x-auto">
                            <table class="table-auto w-full text-left text-sm">
                              <thead class="border-b">
                                <tr>
                                  <th scope="col" class="p-2">{{ $t('messages.upload.tehingId') }}</th>
                                  <th scope="col" class="p-2">{{ $t('messages.upload.address') }}</th>
                                  <th scope="col" class="p-2">{{ $t('messages.upload.katastritunnused') }}</th>
                                  <th scope="col" class="p-2">{{ $t('messages.upload.error') }}</th>
                                  <th scope="col" class="p-2 text-xs text-gray-500">{{ $t('messages.upload.transactionId') }}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="error in errorList" :key="error.id" class="border-b">
                                  <td class="p-2">{{ error.tehing_id || '-' }}</td>
                                  <td class="p-2">{{ error.tais_aadress || '-' }}</td>
                                  <td class="p-2">{{ error.katastritunnused || '-' }}</td>
                                  <td class="p-2">{{ error.processing_error || '-' }}</td>
                                  <td class="p-2 text-xs text-gray-500">{{ error.id }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </Dialog>

                      <Dialog
                        v-model:visible="showCheckDialog"
                        :header="checkFile?.filename"
                        :style="{ width: '80rem' }"
                        :breakpoints="{ '960px': '95vw' }"
                        data-testid="upload-check-dialog"
                      >
                        <p v-if="checkFile?.check" class="mb-1 text-sm" data-testid="upload-check-summary">
                          {{ $t('messages.upload.check.summary', {
                            onMap: checkFile.check.on_map,
                            fileRows: checkFile.check.file_rows,
                            skipped: checkFile.check.skipped,
                            missing: checkFile.check.unexplained,
                          }) }}
                        </p>
                        <p v-if="checkFile?.check?.pending_elsewhere" class="mb-1 text-sm" data-testid="upload-check-pending">
                          {{ $t('messages.upload.check.pendingSummary', { count: checkFile.check.pending_elsewhere }) }}
                        </p>
                        <p class="mb-3 text-xs text-gray-500">
                          {{ checkFile?.check?.source === 'recorded' ? $t('messages.upload.check.recorded') : $t('messages.upload.check.explain') }}
                        </p>

                        <div v-if="checkLoading" class="flex justify-center items-center py-8">
                          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" />
                        </div>
                        <Message v-else-if="checkFailed" severity="error">{{ $t('messages.upload.check.loadFailed') }}</Message>
                        <template v-else-if="checkDetail">
                          <DataTable :value="checkDetail.cells" size="small" striped-rows data-testid="upload-check-table">
                            <Column field="county" :header="$t('messages.upload.check.county')">
                              <template #body="{ data }">{{ data.county ?? $t('messages.upload.check.unknown') }}</template>
                            </Column>
                            <Column field="month" :header="$t('messages.upload.check.month')">
                              <template #body="{ data }">{{ data.month ?? $t('messages.upload.check.unknown') }}</template>
                            </Column>
                            <Column field="file_rows" :header="$t('messages.upload.check.fileRows')" />
                            <Column field="on_map" :header="$t('messages.upload.check.onMap')" />
                            <Column
                              v-for="reason in CHECK_REASONS"
                              :key="reason"
                              :field="reason"
                              :header="$t(`messages.upload.check.reasons.${reason}`)"
                            >
                              <template #body="{ data }">
                                <span
                                  :class="data[reason] === 0 ? 'text-gray-300' : (MISSING_REASONS.includes(reason) ? 'font-bold text-orange-600' : '')"
                                >{{ data[reason] }}</span>
                              </template>
                            </Column>
                          </DataTable>

                          <p v-if="earlierUploadRows > 0" class="mt-2 text-xs text-gray-500">
                            {{ $t('messages.upload.check.earlierUpload', { count: earlierUploadRows }) }}
                          </p>

                          <template v-if="checkDetail.examples.length">
                            <h6 class="mt-5 mb-2 font-medium">{{ $t('messages.upload.check.rowsNotOnMap') }}</h6>
                            <DataTable :value="checkDetail.examples" size="small" striped-rows data-testid="upload-check-rows">
                              <Column field="row" :header="$t('messages.upload.check.row')">
                                <template #body="{ data }">{{ data.row ?? '–' }}</template>
                              </Column>
                              <Column field="tehing_id" :header="$t('messages.upload.tehingId')">
                                <template #body="{ data }">{{ data.tehing_id ?? '–' }}</template>
                              </Column>
                              <Column field="county" :header="$t('messages.upload.check.county')">
                                <template #body="{ data }">{{ data.county ?? $t('messages.upload.check.unknown') }}</template>
                              </Column>
                              <Column field="month" :header="$t('messages.upload.check.month')">
                                <template #body="{ data }">{{ data.month ?? $t('messages.upload.check.unknown') }}</template>
                              </Column>
                              <Column field="reason" :header="$t('messages.upload.check.reason')">
                                <template #body="{ data }">{{ $t(`messages.upload.check.reasonHints.${data.reason}`) }}</template>
                              </Column>
                            </DataTable>
                            <p v-if="checkDetail.examples.length < checkFile.check.file_rows - checkFile.check.on_map" class="mt-2 text-xs text-gray-500">
                              {{ $t('messages.upload.check.firstRowsOnly', { count: checkDetail.examples.length }) }}
                            </p>
                          </template>
                        </template>
                      </Dialog>
                  </div>
              </TabPanel>
          </TabPanels>
      </Tabs>

    </div>
  </AppLayout>
</template>

<script setup>
import { router, usePoll } from '@inertiajs/vue3'
import axios from 'axios'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowRef,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useUploadCheck } from '@/composables/useUploadCheck'
import AppLayout from '@/Layouts/AppLayout.vue'

const { t } = useI18n()

/** RED-256 reasons a file row is not on the map; the last three should never happen and alert. */
const CHECK_REASONS = ['no_tehing_id', 'duplicate', 'pending_elsewhere', 'not_stored', 'no_location', 'no_map_geometry']
const MISSING_REASONS = ['not_stored', 'no_location', 'no_map_geometry']

const props = defineProps({
  files: {
    type: Array,
    default: () => [],
  },
})

const confirm = useConfirm()
const toast = useToast()

const errorMsg = shallowRef(null)
const showSpinnerGet = shallowRef(false)
const showDetailsDialog = shallowRef(false)
const showTransactionUploadId = shallowRef(null)
const currentErrorType = shallowRef(null)
const errorList = ref([])
const loadingErrors = ref(false)

const canOpenGeocodingDetails = file => {
  const total = Number(file?.items_total ?? 0)
  const processed = Number(file?.items_processed ?? 0)
  const geocoded = Number(file?.items_geocoded_count ?? 0)
  return total > 0 && processed > 0 && geocoded > 0 && geocoded < processed
}

const canOpenEhrDetails = file => {
  const total = Number(file?.items_total ?? 0)
  const processed = Number(file?.items_processed ?? 0)
  const ehrProcessed = Number(file?.ehr_items_processed ?? 0)
  return total > 0 && processed > 0 && ehrProcessed > 0 && ehrProcessed < processed
}

const uploadDetail = computed(() => {
  return props.files.find(file => file.id === showTransactionUploadId.value)
})

const showCheckDialog = shallowRef(false)
const checkFileId = shallowRef(null)
const checkFile = computed(() => props.files.find(file => file.id === checkFileId.value))
const {
  detail: checkDetail,
  loading: checkLoading,
  failed: checkFailed,
  load: loadCheck,
} = useUploadCheck()
const earlierUploadRows = computed(() =>
  (checkDetail.value?.cells ?? []).reduce((sum, cell) => sum + (cell.earlier_upload ?? 0), 0),
)

const openCheckDialog = file => {
  checkFileId.value = file.id
  showCheckDialog.value = true
  loadCheck(file.id)
}

const openDetailsDialog = async (uploadId, errorType = null) => {
  showTransactionUploadId.value = uploadId
  currentErrorType.value = errorType
  errorList.value = []

  if (errorType) {
    await fetchErrors(uploadId, errorType)
  }

  showDetailsDialog.value = true
}

const fetchErrors = async (uploadId, errorType) => {
  loadingErrors.value = true
  try {
    const response = await axios.get(`/api/admin/upload/${uploadId}/errors`, {
      params: { type: errorType },
    })
    errorList.value = response.data.errors || []
  } catch (err) {
    console.error('Failed to fetch errors:', err)
    errorList.value = []
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load error details',
      life: 5000,
    })
  } finally {
    loadingErrors.value = false
  }
}

const selectedFile = ref(null)
const uploadInProgress = ref(false)
const progress = ref(0)
const uploadDisabled = ref(false)
const POLLING_INTERVAL = 10000

const { start, stop } = usePoll(
  POLLING_INTERVAL,
  {
    async: true,
  },
  {
    autoStart: false,
  },
)

onMounted(() => {
  startSync()
})

onBeforeUnmount(() => {
  stop()
})

const fileSelected = event => {
  if (event.files && event.files.length > 0) {
    selectedFile.value = event.files[0]
  } else {
    selectedFile.value = null
  }
}

const clearSelectedFile = () => {
  selectedFile.value = null
}

const uploadFile = async () => {
  if (!selectedFile.value) return

  errorMsg.value = null
  uploadInProgress.value = true
  progress.value = 0

  const formData = new FormData()
  formData.append('file', selectedFile.value)

  try {
    await axios.post(route('upload.upload'), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        progress.value = Math.round(
          (100 * progressEvent.loaded) / progressEvent.total,
        )
      },
    })
    toast.add({
      severity: 'success',
      summary: 'Uploaded',
      detail: 'File uploaded successfully, processing started.',
      life: 3000,
    })

    router.reload()
    start()
  } catch (err) {
    errorMsg.value =
      err.response?.data?.message || err.message || 'Upload failed'
    toast.add({
      severity: 'error',
      summary: 'Upload Failed',
      detail: errorMsg.value,
      life: 5000,
    })
  } finally {
    resetUploadState()
  }
}

const resetUploadState = () => {
  uploadInProgress.value = false
  progress.value = 0
  selectedFile.value = null
}

const deleteFileConfirm = file => {
  confirm.require({
    group: 'deleteConfirm',
    message: t('messages.upload.deleteConfirmMsg'),
    header: t('messages.upload.deleteConfirm'),
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      await deleteFile(file)
    },
    reject: () => {},
  })
}

const deleteFile = async file => {
  try {
    await axios.delete(route('upload.delete', file.id))
    router.reload()
    toast.add({
      severity: 'success',
      summary: 'Deleted',
      detail: 'File deleted successfully',
      life: 3000,
    })
  } catch (err) {
    const deleteError =
      err.response?.data?.message || err.message || 'Failed to delete file'
    toast.add({
      severity: 'error',
      summary: 'Delete Failed',
      detail: deleteError,
      life: 5000,
    })
  }
}

const startSync = async () => {
  const activeFiles = props.files.filter(
    file => file.batch_id && !file.processed_at && !file.failed_at,
  )

  start()
  console.log('Polling started.')

  if (activeFiles.length > 0) {
    if (props.files.every(f => f.processed_at || f.failed_at)) {
      stop()
      console.log('Polling stopped.')
    }
  } else {
    stop()
    console.log('Polling stopped.')
  }
}

const getFileSeverity = file => {
  if (file.processed_at) return 'success'
  if (file.failed_at && file.ehr_items_processed > 0) return 'warn'
  if (file.failed_at) return 'danger'
  if (file.batch_id && !file.processed_at && !file.failed_at) return 'info'
  if (file.processing_started_at && !file.batch_id) return 'secondary'
  return 'secondary'
}

const getFileStatusText = file => {
  if (file.processed_at) return t('messages.status.completed')
  if (file.failed_at && file.ehr_items_processed > 0 && (file.items_total - file.ehr_items_processed - file.items_skipped_duplicates > 0))
    return t('messages.status.some_failed')
  if (file.failed_at && file.ehr_items_processed === 0) return t('messages.status.failed')
  if (file.batch_info?.cancelledAt) return t('messages.status.cancelled')
  if (file.batch_id && !file.batch_info?.finishedAt)
    return t('messages.status.processing')
  if (file.processing_started_at) return t('messages.status.uploading')
  return t('messages.status.uploaded')
}

const canRetry = file => {
  return !!file.failed_at || (!file.processed_at && !file.batch_id)
}

const retryProcessing = async file => {
  errorMsg.value = null

  router.post(
    route('upload.retry', file.id),
    {},
    {
      onSuccess: () => {
        toast.add({
          severity: 'info',
          summary: 'Retry Started',
          detail: `Retrying processing for ${file.filename}`,
          life: 3000,
        })

        start()
      },
      onError: err => {
        const retryError =
          err.response?.data?.message || err.message || 'Failed to start retry'
        toast.add({
          severity: 'error',
          summary: 'Retry Failed',
          detail: retryError,
          life: 5000,
        })
      },
    },
  )
}
</script>

<style scoped>
  @reference '../../../css/tailwind.css';



.table-auto {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 350px;
}
.table-auto tr th,
.table-auto tr td {
  @apply border-r-2 border-b-2 border-gray-100 dark:border-gray-700;
}

.table-auto tr th:first-child,
.table-auto tr td:first-child {
  @apply border-l-2 border-gray-100 dark:border-gray-700;
}
.table-auto tr th {
  @apply border-t-2 border-gray-100 bg-gray-100 dark:bg-gray-800 dark:text-white dark:border-gray-700 text-left;
}

/* bottom-left border-radius */
.table-auto tr:last-child td:first-child {
  border-bottom-left-radius: 6px;
}

/* bottom-right border-radius */
.table-auto tr:last-child td:last-child {
  border-bottom-right-radius: 6px;
}
</style>
